import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import { useAuth, api } from "@/App";

/**
 * Lightweight, native product tour for newly-paid Pro users.
 *
 * Replaces `react-joyride`, which had a persistent bug where its portal/overlay
 * DOM nodes survived unmount and blocked all clicks on the dashboard. This is
 * a plain React component — no portals, no document-level mutations, no
 * spotlights. Just a small bottom-right card the user can dismiss any time
 * with no side effects.
 *
 * Triggered when localStorage.chillrefer_tour_requested === "1" (set by the
 * "Take a 60s tour" chip on Home or the "Replay tour" link in Settings).
 * On finish/skip we POST /api/auth/tour-complete so a backend flag tracks it.
 */

const STEPS = [
  {
    title: "Welcome to ChillRefer 👋",
    body:
      "Quick 60-second tour. We'll show you the 5 screens that matter — Home, Discover, Inbox, Autopilot, and Settings.",
    cta: "Show me around",
  },
  {
    title: "Discover Jobs",
    body:
      "Paste any LinkedIn job URL on the Discover page — we instantly find hiring managers + recruiters most likely to refer you, then queue personalised outreach.",
    cta: "Next",
    nav: "/app/discover",
  },
  {
    title: "Your Inbox",
    body:
      "When recruiters reply to your outreach, conversations land here. Reply directly, or let our AI suggest a response in your tone.",
    cta: "Next",
    nav: "/app/inbox",
  },
  {
    title: "Autopilot",
    body:
      "Flip Autopilot ON and ChillRefer sends invites, follow-ups, and replies on your behalf — within the daily limits you set. Switch OFF anytime for manual control.",
    cta: "Next",
    nav: "/app/autopilot",
  },
  {
    title: "You're set 🚀",
    body:
      "Tweak daily limits, work hours, and AI tone in Settings — or just close this and let ChillRefer run. Check Inbox in 24h for the first replies.",
    cta: "Got it",
    nav: "/app/home",
  },
];

export default function ProductTour() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Open the tour ONCE when the user becomes available (Pro plan + requested).
  // We intentionally don't re-run on every `location.pathname` change — the
  // tour itself navigates between pages, and re-triggering would reset the
  // step counter mid-flow.
  useEffect(() => {
    if (!user || user.plan !== "pro") return;
    if (open) return; // already running — don't restart
    try {
      if (localStorage.getItem("chillrefer_tour_requested") === "1") {
        setOpen(true);
        setStep(0);
      }
    } catch {
      // localStorage might be unavailable in restrictive browsers — silently no-op.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const finish = async () => {
    setOpen(false);
    setStep(0);
    try { localStorage.removeItem("chillrefer_tour_requested"); } catch {}
    try {
      await api.post("/auth/tour-complete");
      await refreshUser();
    } catch {
      // best-effort — never block the user on a tour-complete failure
    }
  };

  const next = () => {
    const n = step + 1;
    if (n >= STEPS.length) {
      finish();
      return;
    }
    const navTo = STEPS[n].nav;
    if (navTo && navTo !== location.pathname) navigate(navTo);
    setStep(n);
  };

  const back = () => {
    if (step === 0) return;
    const p = step - 1;
    const navTo = STEPS[p].nav;
    if (navTo && navTo !== location.pathname) navigate(navTo);
    setStep(p);
  };

  // Early bail-out — nothing rendered, nothing in DOM. Safe & clean.
  if (!user || user.plan !== "pro" || !open) return null;

  const s = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div
      data-testid="product-tour"
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 60,
        width: 360,
        maxWidth: "calc(100vw - 32px)",
        background: "#18181b",
        border: "1px solid rgba(250,204,21,0.22)",
        borderRadius: 14,
        padding: "18px 20px 16px",
        color: "#fff",
        fontFamily: "'Manrope', sans-serif",
        boxShadow: "0 18px 50px rgba(0,0,0,0.55)",
      }}
    >
      {/* Header — close button + progress */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 600 }}>
          <Sparkles size={12} style={{ color: "#FACC15" }} />
          Step {step + 1} of {STEPS.length}
        </div>
        <button
          onClick={finish}
          aria-label="Close tour"
          data-testid="product-tour-close"
          style={{
            background: "transparent",
            border: "none",
            color: "rgba(255,255,255,0.5)",
            cursor: "pointer",
            padding: 4,
            display: "flex",
            alignItems: "center",
          }}
        >
          <X size={14} />
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 999, marginBottom: 14, overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: `${progress}%`,
            background: "#FACC15",
            transition: "width 0.25s ease-out",
          }}
        />
      </div>

      {/* Body */}
      <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0, marginBottom: 6 }} data-testid="product-tour-title">
        {s.title}
      </h3>
      <p style={{ fontSize: 13.5, lineHeight: 1.55, color: "rgba(255,255,255,0.78)", margin: 0, marginBottom: 14 }}>
        {s.body}
      </p>

      {/* Footer — back + next/skip */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button
          onClick={finish}
          data-testid="product-tour-skip"
          style={{
            background: "transparent",
            border: "none",
            color: "rgba(255,255,255,0.45)",
            fontSize: 11.5,
            fontWeight: 600,
            cursor: "pointer",
            padding: 0,
          }}
        >
          Skip tour
        </button>

        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {step > 0 && (
            <button
              onClick={back}
              data-testid="product-tour-back"
              style={{
                background: "transparent",
                border: "none",
                color: "rgba(255,255,255,0.6)",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                padding: "8px 10px",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <ChevronLeft size={13} /> Back
            </button>
          )}
          <button
            onClick={next}
            data-testid="product-tour-next"
            style={{
              background: "#FACC15",
              color: "#000",
              fontWeight: 800,
              fontSize: 12.5,
              padding: "9px 16px",
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {s.cta}
            {step < STEPS.length - 1 && <ChevronRight size={13} />}
          </button>
        </div>
      </div>
    </div>
  );
}
